import type { ApiQuery, ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { MemberRepository } from './repository';
import type { DownlineQuery, MemberListQuery } from './types';

export type MemberIdempotencyKeyFactory = () => string;

export function createRealMemberRepository(
  apiClient: ApiClient,
  accessToken: string | null,
  createIdempotencyKey: MemberIdempotencyKeyFactory = defaultIdempotencyKey,
): MemberRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/members', 'get'>>('/api/v1/members', {
        ...auth,
        query: toMemberListQuery(query),
        signal,
      });
      return {
        items: result.data.data,
        total: result.data.pagination.total,
        requestId: result.requestId,
      };
    },
    async get(memberId, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/members/{id}', 'get'>>(
        memberPath(memberId),
        { ...auth, signal },
      );
      return result.data.data;
    },
    async listDownlines(memberId, query, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/members/{id}/downlines', 'get'>>(
        `${memberPath(memberId)}/downlines`,
        {
          ...auth,
          query: toDownlineQuery(query),
          signal,
        },
      );
      return {
        items: result.data.data,
        total: result.data.pagination.total,
        requestId: result.requestId,
      };
    },
    async listAssignments(memberId, signal) {
      const items: ApiResponse<'/api/v1/members/{id}/assignments', 'get'>['data'] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const result = await apiClient.get<ApiResponse<'/api/v1/members/{id}/assignments', 'get'>>(
          `${memberPath(memberId)}/assignments`,
          {
            ...auth,
            query: { page, pageSize: 100 },
            signal,
          },
        );
        items.push(...result.data.data);
        totalPages = Math.ceil(result.data.pagination.total / result.data.pagination.pageSize);
        page += 1;
      } while (page <= totalPages);
      return items;
    },
    async changeStatus(memberId, input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/members/{id}/status', 'post'>>(
        `${memberPath(memberId)}/status`,
        input,
        auth,
      );
      return mutation(result);
    },
    async bindPartnerUser(memberId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/members/{id}/partner-user-binding', 'post'>
      >(`${memberPath(memberId)}/partner-user-binding`, input, auth);
      return mutation(result);
    },
    async rotateReferralCode(memberId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/members/{id}/referral-code/rotate', 'post'>
      >(`${memberPath(memberId)}/referral-code/rotate`, input, auth);
      return mutation(result);
    },
    async previewAssignment(input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/member-assignment-change-sets/preview', 'post'>
      >('/api/v1/member-assignment-change-sets/preview', input, auth);
      return result.data.data;
    },
    async createAssignmentChangeSet(input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/member-assignment-change-sets', 'post'>
      >('/api/v1/member-assignment-change-sets', input, {
        ...auth,
        idempotencyKey: freshKey(createIdempotencyKey),
      });
      return mutation(result);
    },
    async getAssignmentChangeSet(changeSetId, signal) {
      const result = await apiClient.get<
        ApiResponse<'/api/v1/member-assignment-change-sets/{id}', 'get'>
      >(changeSetPath(changeSetId), { ...auth, signal });
      return result.data.data;
    },
    async cancelAssignmentChangeSet(changeSetId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/member-assignment-change-sets/{id}/cancel', 'post'>
      >(`${changeSetPath(changeSetId)}/cancel`, input, {
        ...auth,
        idempotencyKey: freshKey(createIdempotencyKey),
      });
      return mutation(result);
    },
  };
}

function toMemberListQuery(query: MemberListQuery): ApiQuery<'/api/v1/members', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...present('partnerId', query.filters.partnerId),
    ...present('agentId', query.filters.agentId),
    ...present('uplineMemberId', query.filters.uplineMemberId),
    ...present('memberType', query.filters.memberType),
    ...present('status', query.filters.status),
    ...present('username', query.filters.username),
    ...present('partnerUserId', query.filters.partnerUserId),
    ...present('referralCode', query.filters.referralCode),
  };
}

function toDownlineQuery(query: DownlineQuery): ApiQuery<'/api/v1/members/{id}/downlines', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...present('status', query.filters.status),
    ...present('username', query.filters.username),
    ...present('partnerUserId', query.filters.partnerUserId),
  };
}

function present<Key extends string, Value extends string>(key: Key, value: Value) {
  return value.length === 0 ? {} : ({ [key]: value } as Record<Key, Value>);
}

function mutation<T>(result: {
  readonly data: { readonly data: T };
  readonly requestId: string | null;
}): MutationResult<T> {
  return { data: result.data.data, requestId: result.requestId };
}

function memberPath(memberId: string): string {
  return `/api/v1/members/${encodeURIComponent(memberId)}`;
}

function changeSetPath(changeSetId: string): string {
  return `/api/v1/member-assignment-change-sets/${encodeURIComponent(changeSetId)}`;
}

function freshKey(factory: MemberIdempotencyKeyFactory): string {
  const key = factory().trim();
  if (key.length === 0) throw new Error('Idempotency key factory returned an empty value');
  return key;
}

function defaultIdempotencyKey(): string {
  return crypto.randomUUID();
}
